import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  FaPlusCircle,
  FaPaperclip,
  FaImage,
  FaCamera,
  FaMicrophone,
  FaVolumeUp,
  FaTimes,
} from "react-icons/fa";
import { MdGraphicEq } from "react-icons/md";
import { toast } from "react-toastify";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import * as Sentry from "@sentry/react";
import { UserContext } from "../contexts/UserContext";
import { useClearChat } from "../contexts/ClearChatContext";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const EVALUATE_ANSWERS_FILE = "components/EvaluateAnswers.jsx";
const EVALUATE_RATE_LIMIT_SUPPORT_MESSAGE =
  "You've reached the current evaluation limit. Please wait a few minutes or contact support for higher access if you need more submissions.";

function safeStringify(value, max = 5000) {
  try {
    const json = JSON.stringify(value, (key, val) => {
      if (typeof val === "string" && val.length > 2000) {
        return val.slice(0, 2000) + "…[truncated]";
      }
      return val;
    });
    return json.length > max ? json.slice(0, max) + "…[truncated]" : json;
  } catch {
    return "[Unserializable]";
  }
}

const captureSentryError = (heading, error, extra = {}) => {
  let normalizedError;

  if (error instanceof Error) {
    // Keep original message and stack, trim only if message is huge
    const shortMessage =
      error.message.length > 1000
        ? error.message.slice(0, 1000) + "…[truncated]"
        : error.message;

    normalizedError = new Error(shortMessage);
    normalizedError.name = error.name;
    normalizedError.stack = error.stack;
  } else {
    // Non-Error inputs → wrap into Error
    normalizedError = new Error(
      typeof error === "string" ? error.slice(0, 1000) : heading
    );
  }

  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("component", "EvaluateAnswers");
    scope.setExtra("error_heading", heading);
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    if (!(error instanceof Error)) {
      scope.setExtra("original_error_value", error);
    }
    Sentry.captureException(normalizedError);
  });
};
const captureSentrySuccess = (heading, extra = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel("info");
    scope.setTag("component", "EvaluateAnswers");
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    Sentry.captureMessage(heading);
  });
};
// Camera Modal Component
const CameraModal = ({ isOpen, onClose, onCapture, hideCapture = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modalRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [isOpen]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setError(null);
    } catch (err) {
      captureSentryError(
        `${EVALUATE_ANSWERS_FILE} | CameraModal.startCamera | L49`,
        err,
        {
          facingMode: "environment",
          width: 1280,
          height: 720,
          isOpen,
        }
      );
      console.error("Error accessing camera:", err);
      setError("Camera access denied or not available");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Create a File object from the blob
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });

          onCapture(file);
          onClose();
        }
      },
      "image/jpeg",
      0.9
    );
  };

  // Drag functionality
  const handleMouseDown = (e) => {
    if (e.target.closest(".modal-header")) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep modal within viewport bounds
      const maxX = window.innerWidth - (isMinimized ? 200 : 340);
      const maxY = window.innerHeight - (isMinimized ? 60 : 300);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      onMouseDown={handleMouseDown}
      style={{
        position: "fixed",
        top: position.y,
        left: position.x,
        width: isMinimized ? 200 : 340,
        height: isMinimized ? 60 : "auto",
        zIndex: 100,
        background: "white",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        cursor: isDragging ? "grabbing" : "default",
        transition: isDragging ? "none" : "all 0.3s ease",
      }}
    >
      {/* Draggable Header */}
      <div
        className="modal-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: "#f8fafc",
          borderBottom: isMinimized ? "none" : "1px solid #e5e7eb",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        <h3
          className="text-sm font-semibold"
          style={{
            margin: 0,
            color: "#374151",
            fontSize: isMinimized ? "12px" : "14px",
          }}
        >
          📹 Camera
        </h3>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Minimize/Maximize Button */}
          <button
            onClick={toggleMinimize}
            style={{
              background: "none",
              border: "none",
              fontSize: "14px",
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
            }}
            aria-label={isMinimized ? "Maximize" : "Minimize"}
            title={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? "🔄" : "➖"}
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "16px",
              cursor: "pointer",
              color: "#ef4444",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
            }}
            aria-label="Close camera"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {/* Modal Content - Only show when not minimized */}
      {!isMinimized && (
        <div style={{ padding: "16px" }}>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4" style={{ fontSize: "14px" }}>
                {error}
              </p>
              <button
                onClick={startCamera}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                style={{ fontSize: "14px" }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="text-center w-full">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-60 rounded-lg mb-4"
                style={{
                  maxHeight: "200px",
                  transform: "scaleX(-1)",
                  transformOrigin: "center",
                }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* Action Buttons */}
              {stream && !hideCapture && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                    marginTop: "8px",
                  }}
                >
                  <button
                    onClick={onClose}
                    className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    style={{ fontSize: "12px" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={capturePhoto}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    style={{ fontSize: "12px" }}
                  >
                    📸 Capture
                  </button>
                </div>
              )}

              {/* Live Interaction Message */}
              {stream && hideCapture && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#10b981",
                    marginTop: "8px",
                    padding: "8px",
                    backgroundColor: "#f0fdf4",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  🎥 Live interaction mode - Camera is active for real-time
                  analysis
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EvaluationPage = () => {
  const { userData } = useContext(UserContext);
  const [question, setQuestion] = useState("");
  const [totalMarks, setTotalMarks] = useState(5);
  const [loading, setLoading] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [messages, setMessages] = useState([]);
  // Add state to track whether welcome message should be shown
  const [showWelcome, setShowWelcome] = useState(true);

  // Added file and image handling capabilities
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [voice, setVoice] = useState(null);
  const messagesEndRef = useRef(null);
  // Added camera modal states
  const [showCameraOptions, setShowCameraOptions] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  // Drag and drop & paste support
  const [isDragging, setIsDragging] = useState(false);

  // Add voice/audio playback state and refs
  const audioRef = useRef(null);
  const lastAudioUrlRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Add voice/communication mode state and refs
  const [communicationMode, setCommunicationMode] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isSystemSpeaking, setIsSystemSpeaking] = useState(false);
  const [isVoicePaused, setIsVoicePaused] = useState(false);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const [userModulationOpacities, setUserModulationOpacities] = useState([
    0.5, 0.8, 0.5,
  ]);
  const [systemModulationOpacities, setSystemModulationOpacities] = useState([
    0.5, 0.8, 0.5,
  ]);
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(
      message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE
    );
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  // At the top of the component
  const recognitionRef = useRef(null);
  const voiceSubmitRef = useRef(false);
  const lastSpokenBotMessageRef = useRef(null);

  // Add at the top of the component, after useState declarations
  const questionRef = useRef(question);
  const fileRef = useRef(file);
  const imageRef = useRef(image);
  const imagePreviewRef = useRef(imagePreview);

  const communicationModeRef = useRef(communicationMode);
  useEffect(() => {
    communicationModeRef.current = communicationMode;
  }, [communicationMode]);

  useEffect(() => {
    questionRef.current = question;
  }, [question]);
  useEffect(() => {
    fileRef.current = file;
  }, [file]);
  useEffect(() => {
    imageRef.current = image;
  }, [image]);
  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);

  // Utility to play base64 audio (copied from DoubtSolver)
  const playBase64Audio = (base64String) => {
    if (!base64String) {
      console.log("[Audio] No base64 string provided");
      return;
    }
    console.log(
      "[Audio] Starting to play base64 audio, length:",
      base64String.length
    );

    // Revoke previous audio URL if any
    if (lastAudioUrlRef.current) {
      URL.revokeObjectURL(lastAudioUrlRef.current);
      lastAudioUrlRef.current = null;
    }
    // Convert base64 to Blob
    const byteString = atob(base64String.split(",").pop());
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: "audio/wav" });
    const url = URL.createObjectURL(blob);
    lastAudioUrlRef.current = url;
    setAudioUrl(url);
    setIsSystemSpeaking(true);
    console.log("[Audio] Audio URL created, blob size:", blob.size);

    // Set up audio element with proper event handling
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.onended = () => {
        console.log("[Audio] Audio playback ended");
        console.log("[Voice] Audio ended, setting isSystemSpeaking to false");
        // The main useEffect will handle starting user recognition when isSystemSpeaking becomes false
        setIsSystemSpeaking(false);
      };
      audioRef.current.onerror = (event) => {
        captureSentryError(
          `${EVALUATE_ANSWERS_FILE} | playBase64Audio::audio.onerror | L422`,
          new Error(
            `Audio element error: ${event?.target?.error?.code ?? "unknown"}`
          ),
          {
            audioErrorCode: event?.target?.error?.code ?? null,
            audioErrorMessage: event?.target?.error?.message ?? null,
            base64Length: base64String?.length ?? 0,
          }
        );
        console.error("[Audio] Audio playback error:", event);
        console.log("[Voice] Audio error, setting isSystemSpeaking to false");
        setIsSystemSpeaking(false);
      };
      audioRef.current.onloadstart = () => {
        console.log("[Audio] Audio loading started");
      };
      audioRef.current.oncanplay = () => {
        console.log("[Audio] Audio can play");
      };
    } else {
      console.warn("[Audio] Audio ref is null, will retry in setTimeout");
    }

    // Use setTimeout like in DoubtSolver to ensure audio is ready
    setTimeout(() => {
      if (audioRef.current) {
        console.log("[Audio] Attempting to play audio");
        audioRef.current.play().catch((error) => {
          captureSentryError(
            `${EVALUATE_ANSWERS_FILE} | playBase64Audio::play.catch | L450`,
            error,
            {
              audioReadyState: audioRef.current?.readyState ?? null,
              hasAudioUrl: Boolean(lastAudioUrlRef.current),
              base64Length: base64String?.length ?? 0,
            }
          );
          console.error("[Audio] Error playing audio:", error);
          setIsSystemSpeaking(false);
        });
      } else {
        console.error("[Audio] Audio ref is still null in setTimeout");
        setIsSystemSpeaking(false);
      }
    }, 100);
  };

  // Enhanced function to format chat history for API with better context
  const formatChatHistoryForAPI = (messages) => {
    return messages.map((msg) => {
      // Convert timestamp to integer if it's an ISO string
      let timestamp = msg.timestamp || Date.now();
      if (typeof timestamp === "string") {
        timestamp = new Date(timestamp).getTime();
      }

      return {
        role: msg.type === "user" ? "user" : "assistant",
        content: msg.text,
        timestamp: timestamp,
        // Include metadata for better context
        ...(msg.image && {
          hasImage: true,
          imageName: msg.imageName || "uploaded_image",
          imageContext: "User uploaded an image with this message",
        }),
        ...(msg.fileName && {
          hasFile: true,
          fileName: msg.fileName,
          fileType: msg.fileType || "unknown",
          fileContext: "User uploaded a document with this message",
        }),
        ...(msg.isVoiceInput && {
          hasVoice: true,
          voiceContext: "User provided voice input for this message",
        }),
        // Add evaluation-specific metadata
        ...(msg.score !== undefined && {
          hasScore: true,
          score: msg.score,
          totalMarks: msg.totalMarks,
          evaluationContext: "This message contains evaluation results",
        }),
      };
    });
  };

  // Enhanced function to get contextual chat history
  const getRecentChatHistory = (allMessages, maxMessages = 15) => {
    // Get more recent messages to maintain better context
    const recentMessages = allMessages.slice(-maxMessages);
    const formattedHistory = formatChatHistoryForAPI(recentMessages);

    // Add conversation summary if chat is getting long
    if (allMessages.length > maxMessages) {
      const olderMessages = allMessages.slice(0, -maxMessages);
      const conversationSummary = generateConversationSummary(olderMessages);

      return {
        conversationSummary,
        recentHistory: formattedHistory,
        totalMessages: allMessages.length,
      };
    }

    return {
      recentHistory: formattedHistory,
      totalMessages: allMessages.length,
    };
  };

  // Helper function to generate conversation summary for long chats
  const generateConversationSummary = (olderMessages) => {
    const subjects = [];
    const topics = [];
    const evaluationHistory = [];

    olderMessages.forEach((msg) => {
      // Extract key topics (simplified approach)
      const words = msg.text.toLowerCase().split(" ");
      const keyWords = words.filter(
        (word) =>
          word.length > 4 &&
          ![
            "what",
            "how",
            "why",
            "when",
            "where",
            "which",
            "could",
            "would",
            "should",
          ].includes(word)
      );
      topics.push(...keyWords.slice(0, 3)); // Take first 3 relevant words

      // Track evaluation history
      if (msg.type === "bot" && msg.score !== undefined) {
        // Convert timestamp to integer if it's an ISO string
        let timestamp = msg.timestamp;
        if (typeof timestamp === "string") {
          timestamp = new Date(timestamp).getTime();
        }

        evaluationHistory.push({
          score: msg.score,
          totalMarks: msg.totalMarks,
          timestamp: timestamp,
        });
      }
    });

    return {
      subjects: [...new Set(subjects)],
      topics: [...new Set(topics)].slice(0, 10), // Top 10 unique topics
      messageCount: olderMessages.length,
      evaluationHistory: evaluationHistory.slice(-5), // Last 5 evaluations
    };
  };

  // Function to auto-resize textarea - copied from first code
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate the new height based on content
      const minHeight = 80; // Minimum height
      const maxHeight = 400; // Maximum height before scrolling
      const newHeight = Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight)
      );

      // Set the new height
      textarea.style.height = `${newHeight}px`;
    }
  };

  // Handle text input change
  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
    setTimeout(autoResizeTextarea, 0);
  };

  // Handle mark selection
  const handleMarkSelection = (mark) => {
    setTotalMarks(mark);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Auto-resize when question changes
  useEffect(() => {
    autoResizeTextarea();
  }, [question]);

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      // Update the question field to indicate file was uploaded
      setQuestion(
        (prev) =>
          `${prev ? prev + " " : ""}Uploaded document: ${uploadedFile.name}`
      );

      // Focus on textarea for additional clarification
      setTimeout(() => {
        textareaRef.current?.focus();
        autoResizeTextarea();
      }, 100);
    }
  };

  // Handle image click - show camera options dropdown
  const handleImageClick = () => {
    setShowCameraOptions(!showCameraOptions);
  };

  // Handle gallery selection
  const handleGalleryClick = () => {
    imageInputRef.current.click();
    setShowCameraOptions(false);
  };

  // Handle camera selection
  const handleCameraClick = () => {
    setShowCameraModal(true);
    setShowCameraOptions(false);
  };

  // Handle camera capture
  const handleCameraCapture = (file) => {
    setImage(file);
    // Create URL for image preview
    const imageUrl = URL.createObjectURL(file);
    setImagePreview(imageUrl);
    // Only set 'evaluate this problem' if question is empty
    setQuestion((prev) =>
      prev && prev.trim() ? prev : "evaluate this problem"
    );
    // Focus on textarea for additional clarification
    setTimeout(() => {
      textareaRef.current?.focus();
      autoResizeTextarea();
    }, 100);
  };

  // Handle image upload from gallery
  const handleImageChange = (e) => {
    const uploadedImage = e.target.files[0];
    if (uploadedImage) {
      setImage(uploadedImage);
      // Create URL for image preview
      const imageUrl = URL.createObjectURL(uploadedImage);
      setImagePreview(imageUrl);
      // Only set 'evaluate this problem' if question is empty
      setQuestion((prev) =>
        prev && prev.trim() ? prev : "evaluate this problem"
      );
      // Focus on textarea for additional clarification
      setTimeout(() => {
        textareaRef.current?.focus();
        autoResizeTextarea();
      }, 100);
    }
  };

  // Handle removing image
  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
  };

  // Handle removing file
  const handleRemoveFile = () => {
    setFile(null);
  };

  // Handle text formatting
  const handleTextFormatting = () => {
    // In a real implementation, you would implement text formatting
    // like bold, italic, etc. for the textarea
    console.log("Text formatting clicked");
  };

  // Handle paste (Ctrl+V)
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        handleImageChange({ target: { files: [file] } });
        e.preventDefault();
        break;
      } else if (items[i].kind === "file") {
        const file = items[i].getAsFile();
        handleFileChange({ target: { files: [file] } });
        e.preventDefault();
        break;
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        handleImageChange({ target: { files: [file] } });
      } else {
        handleFileChange({ target: { files: [file] } });
      }
    }
  };

  const getEvaluationApiErrorMessage = (error) => {
    if (!error) {
      return "Sorry, an error occurred during evaluation. Please try again.";
    }
    const errorName = error?.name;
    if (errorName === "AbortError" || errorName === "TimeoutError") {
      return "The evaluation request timed out. Please try again.";
    }
    return "Sorry, an error occurred during evaluation. Please try again.";
  };

  const resetVoiceStateAfterFailure = (shouldPauseVoice) => {
    setIsSystemSpeaking(false);
    if (shouldPauseVoice) {
      setIsUserSpeaking(false);
      setIsVoicePaused(true);
    }
  };

  // Process user input and send to backend
  const processInput = async (
    event,
    questionOverride,
    conversationModeOverride
  ) => {
    const questionToUse =
      typeof questionOverride === "string" ? questionOverride : question;
    const conversationModeToUse =
      typeof conversationModeOverride === "boolean"
        ? conversationModeOverride
        : communicationMode;
    if ((!questionToUse.trim() && !file && !image && !voice) || loading) {
      toast.info("Please enter a question or upload content to evaluate");
      return;
    }

    setLoading(true);

    // Hide welcome message after first input
    setShowWelcome(false);

    // Create a message object to display in the UI
    const newUserMessage = {
      type: "user",
      text: questionToUse.trim() || "",
      timestamp: new Date().toISOString(),
    };

    // Add file info to message if present
    if (file) {
      newUserMessage.fileName = file.name;
      newUserMessage.fileType = file.type;
    }

    // Add image to message if present
    if (image) {
      newUserMessage.image = imagePreview;
      newUserMessage.imageName = image.name;
    }

    // Store media references before clearing input
    const tempImage = image;
    const tempImagePreview = imagePreview;
    const tempFile = file;
    const tempVoice = voice;
    const tempQuestion = questionToUse;
    const tempTotalMarks = totalMarks != null ? totalMarks : 5;

    // Add message to chat and get updated messages for context
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);

    // Clear inputs
    setQuestion("");
    setFile(null);
    setImage(null);
    setImagePreview(null);
    setVoice(null);

    // Reset textarea height after clearing text
    setTimeout(autoResizeTextarea, 0);

    // Get chat history context
    const chatContext = getRecentChatHistory(updatedMessages);

    try {
      let response;
      let endpointUsed = null;
      const handleRateLimitResponse = (message) => {
        setLoading(false);
        const normalizedMessage =
          message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "bot",
            text: normalizedMessage,
            isError: true,
            timestamp: new Date().toISOString(),
            contextual: true,
          },
        ]);
        if (conversationModeToUse) {
          console.log(
            "[Voice] Rate limited, setting voice to resume listening state"
          );
        }
        resetVoiceStateAfterFailure(conversationModeToUse);
        showRateLimitModal(normalizedMessage);
      };
      // If there is an image or file, use with-image endpoint, else use evaluate-text
      if (tempImage || tempFile) {
        // Create FormData for multipart requests
        const formData = new FormData();
        formData.append("question", tempQuestion || "");
        formData.append("totalMarks", String(tempTotalMarks));
        formData.append("studentAnswer", tempQuestion || "");
        formData.append("chatHistory", JSON.stringify(chatContext));
        formData.append("currentSubject", null);
        formData.append("board", userData?.board || "");
        formData.append("grades", userData?.grade || "");
        // Handle different types of files
        if (tempFile) {
          if (tempFile.type && tempFile.type.startsWith("image/")) {
            formData.append("questionImage", tempFile);
          } else {
            formData.append("file", tempFile);
          }
        }
        if (tempImage) {
          formData.append("answerImage", tempImage);
        }
        // Add conversationMode only if it's a boolean
        formData.append("conversationMode", !!conversationModeToUse);
        // Only append prompt if not null/undefined
        if (typeof prompt !== "undefined" && prompt !== null) {
          formData.append("prompt", prompt);
        }
        // Log form data for debugging
        console.log(
          "[FormData] totalMarks:",
          tempTotalMarks,
          typeof tempTotalMarks
        );
        for (let [key, value] of formData.entries()) {
          console.log(
            `[FormData] ${key}:`,
            value instanceof File ? value.name : value
          );
        }
        // Make the API request
        endpointUsed = "/evaluate/with-image";
        response = await fetch(`${baseUrl}/evaluate/with-image`, {
          method: "POST",
          body: formData,
          timeout: 300000, // 5 minutes timeout
          signal: AbortSignal.timeout(300000),
        });

        const rateLimitMessage =
          await extractRateLimitMessageFromResponse(response);
        if (rateLimitMessage) {
          handleRateLimitResponse(rateLimitMessage);
          return;
        }
      } else {
        // Simple JSON request for text-only evaluations
        endpointUsed = "/evaluate-text";
        response = await fetch(`${baseUrl}/evaluate-text`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 300000, // 5 minutes timeout
          signal: AbortSignal.timeout(300000),
          body: JSON.stringify({
            question: tempQuestion,
            studentAnswer: tempQuestion, // Use question as the answer
            totalMarks: tempTotalMarks,
            subject: null,
            grade: 10,
            chatHistory: chatContext,
            currentSubject: null,
            board: userData?.board,
            grades: userData?.grade,
            // Add voice context if applicable
            ...(tempVoice && { hasVoiceInput: true }),
            conversationMode: conversationModeToUse,
            prompt: null,
          }),
        });

        const rateLimitMessage =
          await extractRateLimitMessageFromResponse(response);
        if (rateLimitMessage) {
          handleRateLimitResponse(rateLimitMessage);
          return;
        }
      }

      // Handle response
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server response:", response.status, errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      captureSentrySuccess(
        `${EVALUATE_ANSWERS_FILE} | processInput | ${
          endpointUsed || "unknown-endpoint"
        } success`,
        {
          endpoint: endpointUsed,
          status: response.status,
          totalMarks: tempTotalMarks,
          hasImage: Boolean(tempImage),
          hasFile: Boolean(tempFile),
          hasVoice: Boolean(tempVoice),
          conversationMode: Boolean(conversationModeToUse),
        }
      );

      const data = await response.json();

      // Create bot response message
      const botMessage = {
        type: "bot",
        text:
          data.formatted_feedback ||
          data.feedback ||
          "Content evaluated successfully.",
        score: data.score,
        totalMarks: tempTotalMarks,
        timestamp: new Date().toISOString(),
        contextual: true,
      };

      // Add detailed analysis if available
      if (data.detailed_analysis) {
        botMessage.detailedAnalysis = data.detailed_analysis;
      }

      // Add improvement suggestions if available
      if (data.improvement_suggestions) {
        botMessage.improvementSuggestions = data.improvement_suggestions;
      }

      // Add bot message to chat history
      setMessages((prev) => [...prev, botMessage]);

      // Set evaluation result for display
      setEvaluationResult(data);

      // Clean up image URLs
      if (tempImagePreview) {
        URL.revokeObjectURL(tempImagePreview);
      }

      // Play audio if present and communicationMode is active
      if (conversationModeToUse && data?.audio_base64) {
        console.log(
          "[Audio] Backend returned audio_base64, length:",
          data.audio_base64.length
        );
        console.log(
          "[Audio] Audio base64 starts with:",
          data.audio_base64.substring(0, 50)
        );
        playBase64Audio(data.audio_base64);
      } else if (conversationModeToUse) {
        console.log(
          "[Audio] No audio_base64 from backend, using speech synthesis"
        );
        // Use system speech synthesis if no audio_base64
        setIsSystemSpeaking(true);
        const utterance = new window.SpeechSynthesisUtterance(botMessage.text);

        // Configure voice settings like in DoubtSolver
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice =
          voices.find(
            (voice) =>
              (voice.name.toLowerCase().includes("zira") || // Microsoft Zira (female)
                voice.name.toLowerCase().includes("susan") ||
                voice.name.toLowerCase().includes("female") ||
                voice.name.toLowerCase().includes("woman") ||
                voice.name.toLowerCase().includes("samantha") || // macOS female
                voice.name.toLowerCase().includes("google us english")) && // often female
              voice.lang.startsWith("en")
          ) || voices.find((voice) => voice.lang.startsWith("en")); // fallback

        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }

        utterance.rate = 0.9; // Slightly slower for guidance
        utterance.pitch = 1.0;
        utterance.volume = 0.8;

        utterance.onend = () => {
          console.log("[Voice] Speech synthesis ended");
          setIsSystemSpeaking(false);
          // The main useEffect will handle starting user recognition when isSystemSpeaking becomes false
        };

        utterance.onerror = (event) => {
          captureSentryError(
            `${EVALUATE_ANSWERS_FILE} | processInput::speechSynthesis.onerror | L1024`,
            new Error(`Speech synthesis error: ${event?.error ?? "unknown"}`),
            {
              conversationMode: conversationModeToUse,
              botMessageLength: botMessage?.text?.length ?? 0,
            }
          );
          console.error("Speech synthesis error:", event.error);
          console.log(
            "[Voice] Speech synthesis error, setting isSystemSpeaking to false"
          );
          setIsSystemSpeaking(false);
          // The main useEffect will handle starting user recognition when isSystemSpeaking becomes false
        };

        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      captureSentryError(
        `${EVALUATE_ANSWERS_FILE} | processInput | L1042`,
        error,
        {
          communicationMode: conversationModeToUse,
          inputLength: newUserMessage?.text?.length ?? 0,
          hasImage: Boolean(newUserMessage?.image),
          chatHistoryLength: Array.isArray(messages) ? messages.length : null,
        }
      );
      console.error("Error evaluating:", error);

      const errorMessage = getEvaluationApiErrorMessage(error);
      toast.error(errorMessage);
      resetVoiceStateAfterFailure(conversationModeToUse);
      // Add error message to chat
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          type: "bot",
          text: errorMessage,
          isError: true,
          timestamp: new Date().toISOString(),
          contextual: true,
        },
      ]);
    } finally {
      setLoading(false);
      // Focus back on textarea for next input
      textareaRef.current?.focus();
      autoResizeTextarea();
    }
  };

  // Legacy evaluation function - keep for backward compatibility with the marks UI
  const evaluateWithMarks = processInput;

  // Handle Enter key press to submit
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      processInput();
    }
  };

  // Cleanup function for image preview URLs on component unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      // Clean up any message image previews
      messages.forEach((msg) => {
        if (
          msg.type === "user" &&
          msg.image &&
          typeof msg.image === "string" &&
          msg.image.startsWith("blob:")
        ) {
          URL.revokeObjectURL(msg.image);
        }
      });
    };
  }, [imagePreview, messages]);

  // Add cleanup for audio URLs on unmount
  useEffect(() => {
    return () => {
      if (lastAudioUrlRef.current) {
        URL.revokeObjectURL(lastAudioUrlRef.current);
        lastAudioUrlRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  // Animate user modulation bars while user is speaking
  useEffect(() => {
    let interval;
    if (isUserSpeaking) {
      interval = setInterval(() => {
        setUserModulationOpacities([
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
        ]);
      }, 150);
    } else {
      setUserModulationOpacities([0.4, 0.6, 0.4]);
    }
    return () => clearInterval(interval);
  }, [isUserSpeaking]);

  // Animate system modulation bars while system is speaking
  useEffect(() => {
    let interval;
    if (isSystemSpeaking) {
      interval = setInterval(() => {
        setSystemModulationOpacities([
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
        ]);
      }, 150);
    } else {
      setSystemModulationOpacities([0.4, 0.6, 0.4]);
    }
    return () => clearInterval(interval);
  }, [isSystemSpeaking]);

  // Listen/Speak mode management - similar to DoubtSolver
  useEffect(() => {
    console.log("[Voice] Main useEffect triggered:", {
      communicationMode,
      isSystemSpeaking,
      isUserSpeaking,
      isVoicePaused,
    });

    if (!communicationMode) {
      console.log("[Voice] Communication mode disabled, stopping recognition");
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setIsUserSpeaking(false);
      return;
    }

    if (isSystemSpeaking) {
      console.log("[Voice] System is speaking, stopping user recognition");
      // Stop any active user recognition when system starts speaking
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setIsUserSpeaking(false);
      return;
    }

    if (!("webkitSpeechRecognition" in window)) {
      toast.warn("Speech Recognition not supported.");
      return;
    }

    // Don't start recognition if voice is paused (waiting for resume)
    if (isVoicePaused) {
      console.log("[Voice] Voice is paused, waiting for resume");
      return;
    }

    console.log("[Voice] Starting user recognition");

    // Clean up any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    let finalSpeechText = "";
    let silenceTimer = null;
    const SILENCE_TIMEOUT = 3000; // 5 seconds
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true; // Enable continuous listening
    recognition.interimResults = true;
    recognition.lang = "en-US";

    // Function to handle silence timeout
    const handleSilenceTimeout = () => {
      console.log(
        "[Voice] EvaluateAnswers silence timeout reached, stopping recognition"
      );
      recognition.stop();
    };

    // Function to reset silence timer
    const resetSilenceTimer = () => {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      silenceTimer = setTimeout(handleSilenceTimeout, SILENCE_TIMEOUT);
    };

    // Clear input at start
    setQuestion("");
    questionRef.current = "";

    recognition.start();
    setIsUserSpeaking(true);

    // Start the silence timer
    resetSilenceTimer();

    recognition.onresult = (event) => {
      // Reset silence timer when speech is detected
      resetSilenceTimer();

      let finalTranscript = "";
      let interimTranscript = "";

      // Process ALL results from the beginning to accumulate complete text
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Combine final and interim results
      const completeTranscript = finalTranscript + interimTranscript;

      // Update final speech text only when we have new final results
      if (finalTranscript) {
        finalSpeechText = finalTranscript;
      }

      setQuestion(completeTranscript);
      questionRef.current = completeTranscript;
      setTimeout(autoResizeTextarea, 0);
      setIsVoicePaused(false); // Hide Resume Listening when user is speaking
    };
    recognition.onerror = (event) => {
      console.log("[Voice] Recognition error:", event.error);
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      setIsUserSpeaking(false);
      if (
        event.error === "no-speech" &&
        communicationMode &&
        !isSystemSpeaking
      ) {
        setIsVoicePaused(true);
      }
    };
    recognition.onend = async () => {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }

      // Use the final speech text if available, otherwise use current input
      const speechText = finalSpeechText || questionRef.current;
      console.log("[Voice] Recognition ended, final text:", speechText);

      if (communicationMode && speechText.trim()) {
        // Process the voice input using the same logic as processInput
        await processInput(null, speechText.trim(), true);
        setQuestion("");
        questionRef.current = "";
        // Don't set isUserSpeaking to false here - let the rate limiting handler manage it
        // If rate limiting occurs, the handler will set isVoicePaused to true
        // If successful, the speech synthesis will handle the transition
      } else if (communicationMode && !speechText.trim() && !isSystemSpeaking) {
        setIsUserSpeaking(false);
        setIsVoicePaused(true);
      } else {
        setIsUserSpeaking(false);
        setIsVoicePaused(false);
      }
    };
    recognitionRef.current = recognition;

    // Cleanup function
    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, [communicationMode, isSystemSpeaking, isVoicePaused]);

  // Cleanup recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleVoiceClick = () => {
    setCommunicationMode(true);
  };

  const handleResumeListening = () => {
    console.log("[Voice] Resume listening clicked");
    setIsVoicePaused(false);
    // The main useEffect will handle starting recognition when isVoicePaused becomes false
  };

  const handleCloseCommunicationMode = () => {
    setCommunicationMode(false);
    setIsVoicePaused(false);
    setIsUserSpeaking(false);
    setIsSystemSpeaking(false);
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    // Stop and clean up audio playback if system audio is playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
    }
    if (lastAudioUrlRef.current) {
      URL.revokeObjectURL(lastAudioUrlRef.current);
      lastAudioUrlRef.current = null;
    }
    setAudioUrl(null);
  };

  // Reset isVoicePaused if communicationMode is turned off
  useEffect(() => {
    if (!communicationMode && isVoicePaused) {
      setIsVoicePaused(false);
    }
  }, [communicationMode, isVoicePaused]);

  return (
    <MathJaxContext
      config={{
        tex: {
          inlineMath: [
            ["$", "$"],
            ["\\(", "\\)"],
          ],
          displayMath: [
            ["$$", "$$"],
            ["\\[", "\\]"],
          ],
          processEscapes: true,
          processEnvironments: true,
          packages: ["base", "ams", "mhchem"],
        },
      }}
    >
      <div className="p-4 sm:p-6 rounded-3xl max-w-full mx-auto h-[90vh] overflow-y-auto">
        {/* User profile and greeting - Only show if showWelcome is true */}
        {showWelcome && (
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-12 mb-6">
            <img
              src="/images/tutor.webp"
              alt="Aanya"
              className="w-24 h-28 sm:w-30 sm:h-34 object-cover object-top rounded-2xl"
            />
            <div className="pt-0 sm:pt-12 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl font-semibold text-gray-950 leading-tight">
                Hey there, {userData?.name || "Student"}!
                <br />
                How can I help you ?
              </h1>
            </div>
          </div>
        )}

        {/* Chat messages section */}
        {messages.length > 0 && (
          <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${
                  msg.type === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`rounded-2xl px-4 py-3 max-w-[80%] sm:max-w-[70%] shadow-md ${
                    msg.type === "user"
                      ? "bg-indigo-100 text-indigo-900"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {/* Display image if present */}
                  {msg.image && (
                    <div className="mb-2">
                      <img
                        src={msg.image}
                        alt={msg.imageName || "Uploaded image"}
                        className="max-w-full rounded-lg mb-2"
                        style={{ maxHeight: "120px" }}
                      />
                      {msg.imageName && (
                        <div className="text-xs text-gray-500">
                          {msg.imageName}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Display file info if present */}
                  {msg.fileName && !msg.image && (
                    <div className="mb-2 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      <span className="text-xs text-gray-500">
                        {msg.fileName}
                      </span>
                    </div>
                  )}

                  {/* Message text with LaTeX rendering or HTML for bot */}
                  {msg.type === "bot" ? (
                    <div className="text-sm sm:text-base">
                      <style>{`
                        .ai-table-bordered table {
                          border-collapse: collapse;
                          width: 100%;
                          margin-bottom: 1em;
                        }
                        .ai-table-bordered th, .ai-table-bordered td {
                          border: 1px solid #888;
                          padding: 6px 12px;
                          text-align: left;
                        }
                        .ai-table-bordered th {
                          background: #f3f4f6;
                        }
                      `}</style>
                      <div
                        className="ai-table-bordered"
                        dangerouslySetInnerHTML={{
                          __html: (msg.text || "")
                            // Add extra space before section headers (emoji or **)
                            .replace(
                              /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*|<table>)/g,
                              "<br><br>$2"
                            )
                            // Add <br> for all other newlines
                            .replace(/\n/g, "<br>"),
                        }}
                      />
                    </div>
                  ) : (
                    <div className="text-sm sm:text-base whitespace-pre-wrap">
                      <MathJax dynamic>{msg.text}</MathJax>
                    </div>
                  )}

                  {/* Score for bot messages */}
                  {msg.type === "bot" && typeof msg.score !== "undefined" && (
                    <div className="mt-2 font-bold text-sm">
                      Score: {msg.score}/{msg.totalMarks}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Updated Question input area - matching DoubtSolver styling */}
        <div
          className={`relative w-full mb-4 ${
            isDragging
              ? "border-indigo-600 border-dashed border-2 rounded-lg"
              : ""
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <textarea
            ref={textareaRef}
            placeholder="Enter Your Question & Answers for marks evaluation"
            value={question}
            onChange={handleQuestionChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className="w-full min-h-[70px] sm:min-h-[80px] rounded-2xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            style={{ resize: "none" }}
          />

          {/* Button container - positioned inside the textarea box */}
          <div className="absolute bottom-2 left-3 sm:bottom-3 sm:left-4 flex gap-3 sm:gap-4 text-gray-500">
            {/* Updated Plus icon section with all attachment options */}
            <div className="relative">
              <FaPlusCircle
                size={18} // Smaller icon size for mobile
                className={`cursor-pointer ${
                  loading ? "opacity-50" : "hover:text-gray-700"
                }`}
                onClick={!loading ? handleImageClick : undefined}
              />

              {/* Attachment options dropdown */}
              {showCameraOptions && (
                <div className="absolute bottom-7 left-0 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex flex-col gap-1 sm:gap-2 z-10">
                  <button
                    onClick={handleFileUpload}
                    className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm hover:bg-gray-100 rounded"
                  >
                    <FaPaperclip size={14} /> {/* Smaller icon */}
                    Upload File
                  </button>
                  <button
                    onClick={handleGalleryClick}
                    className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm hover:bg-gray-100 rounded"
                  >
                    <FaImage size={14} /> {/* Smaller icon */}
                    Gallery
                  </button>
                  <button
                    onClick={handleCameraClick}
                    className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm hover:bg-gray-100 rounded"
                  >
                    <FaCamera size={14} /> {/* Smaller icon */}
                    Camera
                  </button>
                </div>
              )}
            </div>

            {/* Hidden file inputs */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
            />
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />
          </div>

          <div className="absolute bottom-2 right-3 sm:bottom-3 sm:right-4 flex gap-2 items-center">
            {communicationMode &&
            !isSystemSpeaking &&
            !isUserSpeaking &&
            isVoicePaused ? (
              <button
                type="button"
                className="flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-yellow-200 hover:bg-yellow-300 text-yellow-900"
                onClick={!loading ? handleResumeListening : undefined}
                disabled={loading}
                title="Resume Listening"
                style={{ minWidth: 32 }}
              >
                <MdGraphicEq size={18} style={{ display: "block" }} /> Resume
                Listening
              </button>
            ) : (
              <button
                type="button"
                className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8
                  ${
                    communicationMode && isUserSpeaking
                      ? "bg-green-200 hover:bg-green-300 text-green-900"
                      : isVoicePaused
                      ? "bg-yellow-200 hover:bg-yellow-300 text-yellow-900"
                      : "bg-gray-200 hover:bg-indigo-200 text-gray-700"
                  }`}
                onClick={
                  !loading && !isSystemSpeaking
                    ? isVoicePaused
                      ? handleResumeListening
                      : handleVoiceClick
                    : undefined
                }
                disabled={loading || isSystemSpeaking}
                title={
                  communicationMode && isUserSpeaking ? "Listening..." : "Voice"
                }
                style={{ minWidth: 32 }}
              >
                <MdGraphicEq size={18} style={{ display: "block" }} />
                {communicationMode && isUserSpeaking ? "Listening..." : null}
              </button>
            )}
          </div>
        </div>

        {/* Communication bar UI (system/user modulation, close button, etc.) */}
        {communicationMode && (
          <div className="w-full flex justify-center mb-4 flex-shrink-0">
            <div className="w-full bg-white rounded-2xl p-2 flex items-center justify-between  relative text-gray-900">
              {/* System modulation (right) */}
              <div className="flex-1 flex flex-col items-center">
                <div className="mb-2 text-xs text-gray-500">System</div>
                {/* Dynamic system voice modulation */}
                <div className="flex gap-2 items-end">
                  <div
                    className="bg-indigo-400 rounded-xl"
                    style={{
                      width: "24px",
                      height: "18px",
                      opacity: systemModulationOpacities[0],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                  <div
                    className="bg-indigo-500 rounded-xl"
                    style={{
                      width: "24px",
                      height: "22px",
                      opacity: systemModulationOpacities[1],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                  <div
                    className="bg-indigo-400 rounded-xl"
                    style={{
                      width: "24px",
                      height: "18px",
                      opacity: systemModulationOpacities[2],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                </div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="mb-2 text-xs text-gray-500">You</div>
                {/* Dynamic user voice modulation */}
                <div className="flex gap-2 items-end">
                  <div
                    className="bg-green-400 rounded-xl"
                    style={{
                      width: "24px",
                      height: "18px",
                      opacity: userModulationOpacities[0],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                  <div
                    className="bg-green-500 rounded-xl"
                    style={{
                      width: "24px",
                      height: "22px",
                      opacity: userModulationOpacities[1],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                  <div
                    className="bg-green-400 rounded-xl"
                    style={{
                      width: "24px",
                      height: "18px",
                      opacity: userModulationOpacities[2],
                      transition: "opacity 0.1s",
                    }}
                  ></div>
                </div>
              </div>
              {/* Close button only */}
              <div className="absolute top-3 right-3">
                <button
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={handleCloseCommunicationMode}
                  title="Close"
                  aria-label="Close voice mode"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Evaluation results section for compatibility with original code */}
        {evaluationResult && messages.length === 0 && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white border border-gray-200 rounded-lg">
            {/* <h3 className="text-lg sm:text-xl font-bold mb-2">Evaluation Result</h3> */}
            <div className="text-sm sm:text-base text-gray-700">
              {evaluationResult.feedback}
            </div>
            <div className="mt-2 font-bold text-sm">
              Overall Score: {evaluationResult.score}/{totalMarks}
            </div>
          </div>
        )}

        {/* Image preview area */}
        {imagePreview && (
          <div className="w-full border border-gray-300 rounded-lg mb-3 sm:mb-4 bg-white shadow-sm p-2 sm:p-3 relative">
            <img
              src={imagePreview}
              alt="Preview"
              className="max-w-full rounded-lg"
              style={{ maxHeight: "100px" }} // Adjusted max height for mobile preview
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="text-xs text-gray-500 mt-1">{image?.name}</div>
          </div>
        )}

        {/* File preview area */}
        {file && !imagePreview && (
          <div className="w-full border border-gray-300 rounded-lg mb-3 sm:mb-4 bg-white shadow-sm p-2 sm:p-3 relative">
            <div className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
              </svg>
              <span className="text-sm ml-2">{file.name}</span>
            </div>
            <button
              onClick={handleRemoveFile}
              className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}

        {/* Updated Marks selector and evaluation button */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center border border-indigo-200 rounded-full px-4 py-2 sm:px-6 sm:py-2">
            <div className="text-sm sm:text-base font-medium text-gray-800 mr-2 sm:mr-3">
              Total Marks:
            </div>
            <div className="flex gap-1 sm:gap-2">
              {[1, 2, 3, 5].map((mark) => (
                <button
                  key={mark}
                  onClick={() => handleMarkSelection(mark)}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-colors ${
                    totalMarks === mark
                      ? "bg-indigo-500 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  {mark}
                </button>
              ))}
            </div>
          </div>
          <div>
            <button
              onClick={processInput}
              disabled={loading}
              className={`px-4 py-2 sm:px-6 sm:py-3 ${
                loading ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
              } text-white font-bold rounded-full transition-colors text-sm sm:text-base`}
            >
              {loading ? "Processing..." : "Evaluate with Marks"}
            </button>
          </div>
        </div>

        {/* Click outside to close camera options */}
        {showCameraOptions && (
          <div
            className="fixed inset-0 z-5"
            onClick={() => setShowCameraOptions(false)}
          />
        )}

        {/* Camera Modal */}
        <CameraModal
          isOpen={showCameraModal}
          onClose={() => setShowCameraModal(false)}
          onCapture={handleCameraCapture}
        />
        <RateLimitModal
          isOpen={isRateLimitModalOpen}
          serverMessage={rateLimitServerMessage}
          supportMessage={EVALUATE_RATE_LIMIT_SUPPORT_MESSAGE}
          onClose={handleRateLimitModalClose}
        />

        {/* Hidden audio element at the bottom of the component's return */}
        <audio
          ref={audioRef}
          src={audioUrl || null}
          preload="auto"
          controls={false}
          style={{ display: "none" }}
        />
      </div>
    </MathJaxContext>
  );
};

export default EvaluationPage;
